import React from 'react';
import Farm2StreakHelper from './feedHelpers/StreakHelper';
import StreakFlame from './StreakFlame';

/**
 * The signed-in player's own share-streak flame, for the avatar in the site nav.
 *
 * Self-contained on purpose. NavComponent is shared by the whole site and every
 * game, so it should not have to know anything about FarmVille 2 streaks beyond
 * rendering this. All the fetching, subscribing and teardown lives here.
 *
 * Inert unless the FarmVille 2 game iframe is on the page. StreakHelper reaches
 * the game by posting to #farm2-game-frame, so off the play page there is nothing
 * to ask — and without this gate the helper would ping twenty times and back off
 * on every other page of the site.
 *
 * Renders nothing when the player has no streak, so the nav is untouched for
 * anyone who has not shared.
 *
 * @param {object} props
 * @param {string} props.zid the signed-in player's zid
 */
/** How long to keep watching for the game iframe to appear, and how often. */
const FRAME_WATCH_INTERVAL_MS = 750;
const FRAME_WATCH_MAX_ATTEMPTS = 20;

class OwnStreakFlame extends React.Component {
    constructor(props) {
        super(props);
        this.state = { streak: null };
        this.onStreakUpdate = this.onStreakUpdate.bind(this);
        this.unsubscribe = null;
        this.fetched = false;
        this.frameWatch = null;
        this.frameWatchAttempts = 0;
    }

    /** Is the FarmVille 2 game frame on this page? */
    gameFramePresent() {
        return typeof document !== "undefined"
            && document.getElementById("farm2-game-frame") !== null;
    }

    componentDidMount() {
        // Subscribing is pure bookkeeping — no network, no pings — so it happens
        // unconditionally. That way a push after the player shares always lands,
        // regardless of what the DOM looked like when this mounted.
        this.unsubscribe = Farm2StreakHelper.Subscribe(this.onStreakUpdate);

        // Fetching is what must wait for the game frame, because StreakHelper reaches
        // the game by posting to it.
        if (!this.tryFetch()) {
            this.startFrameWatch();
        }
    }

    componentDidUpdate() {
        // Cheap opportunistic retry when the nav happens to re-render.
        this.tryFetch();
    }

    componentWillUnmount() {
        this.stopFrameWatch();
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
    }

    /**
     * Request the player's own streak, if we can and have not already.
     * @return {boolean} true once the request has been made
     */
    tryFetch() {
        if (this.fetched) {
            return true;
        }
        if (!this.props.zid || !this.gameFramePresent()) {
            return false;
        }

        this.fetched = true;
        this.stopFrameWatch();

        Farm2StreakHelper.Fetch(["" + this.props.zid], this.onStreakUpdate);
        return true;
    }

    /**
     * Poll briefly for the game iframe to appear.
     *
     * The nav is rendered as a sibling of <Content> and comes first (index.js:1331
     * vs 1414), so React mounts it before the routed page — and therefore before the
     * game iframe exists. Checking once in componentDidMount always failed.
     *
     * componentDidUpdate covers this only if the nav happens to re-render after the
     * page mounts, which nothing guarantees for a sibling subtree. This watch removes
     * that dependency. It stops on the first successful fetch, and gives up after
     * ~15s so pages without the game cost twenty DOM lookups and nothing more.
     */
    startFrameWatch() {
        if (this.frameWatch !== null) {
            return;
        }
        this.frameWatchAttempts = 0;
        this.frameWatch = setInterval(() => {
            this.frameWatchAttempts++;
            if (this.tryFetch()) {
                return; // tryFetch already stopped the watch
            }
            if (this.frameWatchAttempts >= FRAME_WATCH_MAX_ATTEMPTS) {
                this.stopFrameWatch();
            }
        }, FRAME_WATCH_INTERVAL_MS);
    }

    stopFrameWatch() {
        if (this.frameWatch !== null) {
            clearInterval(this.frameWatch);
            this.frameWatch = null;
        }
    }

    /**
     * @param {object} map zid -> streak
     */
    onStreakUpdate(map) {
        if (!map || !this.props.zid) {
            return;
        }
        const mine = map["" + this.props.zid];
        if (!mine) {
            return;
        }

        // Skip the update when nothing actually changed, so a repeated delivery
        // does not cause a pointless nav re-render.
        const current = this.state.streak;
        if (current
            && current.currentStreak === mine.currentStreak
            && current.flameTier === mine.flameTier
            && current.graceActive === mine.graceActive) {
            return;
        }

        this.setState({ streak: mine });
    }

    render() {
        if (!this.state.streak) {
            return null;
        }
        return (
            <StreakFlame
                streak={this.state.streak}
                extraClass="farm2-streak-flame-nav"
            />
        );
    }
}

export default OwnStreakFlame;
